/**
 * Boundary por região (§12.8; nomes da tabela de §12.8 e, dentro delas, os de §7.1 por DE-26). Captura o erro da
 * subárvore (`useErrorBoundary`) e registra `console.error('[app] {name}:', err)` (sem telemetria de terceiros); mostra
 * `fallback`, ou nada com `quiet`, ou o OopsCard; passados `retryMs` (4 s; o mapa usa 2 s), reseta e tenta de novo,
 * em ciclo enquanto falhar. O estado global vive nos sinais, fora da árvore: remontar não perde relógio, feed, replay
 * nem rota (AC-12.8-04). Sem .css (FE §2.3.4: exceção de ErrorBoundary).
 */
import type { ComponentChildren, ComponentType } from 'preact';
import { useEffect, useErrorBoundary, useState } from 'preact/hooks';
import { OopsCard } from './OopsCard';

export interface ErrorBoundaryProps {
  /** Aparece no log: `console.error(\`[app] ${name}:\`, err)`. */
  name: string;
  /** true: não renderiza nada enquanto falha. */
  quiet?: boolean;
  /** Padrão 4000; o mapa usa 2000. */
  retryMs?: number;
  /** Substitui o OopsCard (ex.: cabeçalho mínimo do `header`). */
  fallback?: ComponentType;
  children?: ComponentChildren;
}

export const DEFAULT_RETRY_MS = 4000;

/** Boundaries montadas agora, por nome (contagem): AC-12.8-05 percorre a árvore renderizada em cada modo. */
const mounted = new Map<string, number>();

/** Nomes das boundaries montadas, em ordem alfabética, sem repetição. */
export function mountedBoundaryNames(): string[] {
  return [...mounted.keys()].sort();
}

export function ErrorBoundary({
  name,
  quiet = false,
  retryMs = DEFAULT_RETRY_MS,
  fallback: Fallback,
  children,
}: ErrorBoundaryProps) {
  const [failures, setFailures] = useState(0);
  const [error, reset] = useErrorBoundary((err: unknown) => {
    console.error(`[app] ${name}:`, err);
    setFailures((n) => n + 1);
  });

  useEffect(() => {
    mounted.set(name, (mounted.get(name) ?? 0) + 1);
    return () => {
      const n = (mounted.get(name) ?? 1) - 1;
      if (n <= 0) mounted.delete(name);
      else mounted.set(name, n);
    };
  }, [name]);

  // Um ciclo por falha: mesmo um erro idêntico na nova tentativa agenda outro reset.
  useEffect(() => {
    if (failures === 0 || error === undefined) return;
    const id = setTimeout(reset, retryMs);
    return () => clearTimeout(id);
  }, [failures, retryMs]);

  if (error !== undefined) {
    if (Fallback) return <Fallback />;
    return quiet ? null : <OopsCard />;
  }
  return <>{children}</>;
}
